'use client';

import { c as _c } from "react/compiler-runtime";
import type { User } from '@/lib/auth-client';
import { useForm } from '@tanstack/react-form';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldGroup } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useCreateMessage } from '@/hooks/queries/message.query';
import { useSignOut } from '@/hooks/use-sign-out';
type MessageBoxProps = {
  user: User;
};
export function MessageBox(props) {
  const $ = _c(41);
  const {
    user
  } = props;
  const t = useTranslations();
  const signOut = useSignOut();
  let t0;
  if ($[0] !== t) {
    t0 = z.object({
      message: z.string().min(1, t("error.message-cannot-be-empty"))
    });
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const GuestbookFormSchema = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      message: ""
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== GuestbookFormSchema) {
    t2 = {
      onSubmit: GuestbookFormSchema
    };
    $[3] = GuestbookFormSchema;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const form = useForm({
    defaultValues: t1,
    validators: t2,
    onSubmit: t3 => {
      const {
        value
      } = t3;
      if (isCreating) {
        return;
      }
      createMessage({
        message: value.message
      });
    }
  });
  let t4;
  if ($[5] !== form || $[6] !== t) {
    t4 = () => {
      form.reset();
      toast.success(t("success.message-created"), {
        testId: "guestbook-message-created-toast"
      });
    };
    $[5] = form;
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    mutate: t5,
    isPending: t6
  } = useCreateMessage(t4);
  const createMessage = t5;
  const isCreating = t6;
  let t7;
  if ($[8] !== form) {
    t7 = async function handleSubmit(event) {
      event.preventDefault();
      ;
      try {
        await form.handleSubmit();
      } catch (t8) {
        const error = t8;
        console.error("Failed to submit message:", error);
      }
    };
    $[8] = form;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleSubmit = t7;
  let t8;
  if ($[10] !== user.id || $[11] !== user.image || $[12] !== user.name) {
    t8 = <UserAvatar id={user.id} name={user.name} image={user.image} size="lg" />;
    $[10] = user.id;
    $[11] = user.image;
    $[12] = user.name;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== isCreating || $[15] !== t) {
    t9 = field => {
      const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
      return <Field data-invalid={isInvalid}><Textarea id={field.name} name={field.name} value={field.state.value} onBlur={field.handleBlur} onChange={e => {
          field.handleChange(e.target.value);
        }} aria-invalid={isInvalid} placeholder={t("guestbook.placeholder")} disabled={isCreating} data-testid="guestbook-textarea" />{isInvalid && <FieldError errors={field.state.meta.errors} />}</Field>;
    };
    $[14] = isCreating;
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== form.Field || $[18] !== t9) {
    t10 = <FieldGroup><form.Field name="message">{t9}</form.Field></FieldGroup>;
    $[17] = form.Field;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t) {
    t11 = t("common.sign-out");
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== isCreating || $[23] !== signOut || $[24] !== t11) {
    t12 = <Button variant="outline" onClick={signOut} disabled={isCreating}>{t11}</Button>;
    $[22] = isCreating;
    $[23] = signOut;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("guestbook.submit");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== isCreating || $[29] !== t13) {
    t14 = <Button type="submit" disabled={isCreating} data-testid="guestbook-submit-button">{t13}</Button>;
    $[28] = isCreating;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t12 || $[32] !== t14) {
    t15 = <div className="flex items-center justify-end gap-2">{t12}{t14}</div>;
    $[31] = t12;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== handleSubmit || $[35] !== t10 || $[36] !== t15) {
    t16 = <form onSubmit={handleSubmit} className="w-full space-y-4">{t10}{t15}</form>;
    $[34] = handleSubmit;
    $[35] = t10;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16 || $[39] !== t8) {
    t17 = <div className="flex gap-3">{t8}{t16}</div>;
    $[38] = t16;
    $[39] = t8;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
}